// Vite dynamic glob imports
const mdModules = import.meta.glob('/src/events/*.md', { query: '?raw', import: 'default', eager: true });
const imageModules = import.meta.glob('/src/events/*.jpg', { eager: true, import: 'default' });

/**
 * Parses YAML frontmatter and body from markdown content
 */
function parseMarkdown(rawContent) {
  const frontmatterRegex = /^---\r?\n([\s\S]*?)\r?\n---/;
  const match = rawContent.match(frontmatterRegex);
  if (!match) return { metadata: {}, body: rawContent };

  const fmText = match[1];
  const body = rawContent.slice(match[0].length).trim();
  const metadata = {};

  fmText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > -1) {
      const key = line.slice(0, colonIndex).trim();
      let val = line.slice(colonIndex + 1).trim();
      // Remove surrounding quotes if any
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1);
      }
      metadata[key] = val;
    }
  });

  return { metadata, body };
}

/**
 * Parses timezone offset from the ISO date string and returns a Date object shifted
 * to match the original local time when formatted in UTC.
 */
function getDateInOriginalTimezone(dateStr) {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  const offsetMatch = dateStr.match(/([+-])(\d{2}):?(\d{2})$/);
  let offsetMinutes = 0;
  if (offsetMatch) {
    const sign = offsetMatch[1] === '+' ? 1 : -1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = parseInt(offsetMatch[3], 10);
    offsetMinutes = sign * (hours * 60 + minutes);
  } else if (dateStr.endsWith('Z')) {
    offsetMinutes = 0;
  } else {
    return date;
  }

  return new Date(date.getTime() + offsetMinutes * 60 * 1000);
}

/**
 * Format date for upcoming event display (e.g., "11 April 2026")
 */
function formatUpcomingDate(dateStr) {
  if (!dateStr || dateStr === 'null') return 'DATE - TBC';
  const localDate = getDateInOriginalTimezone(dateStr);
  if (!localDate) return 'DATE - TBC';
  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
  return formatter.format(localDate);
}

/**
 * Format past event metadata string (e.g., "28 June 2026 · The Yogahouse, Bandra, Mumbai")
 */
function formatPastMeta(metadata) {
  const localDate = getDateInOriginalTimezone(metadata.date);
  let datePart = '';
  if (localDate) {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
    datePart = formatter.format(localDate);
  }
  const locationPart = [metadata.location_name, metadata.location_city].filter(Boolean).join(', ');
  return [datePart, locationPart].filter(Boolean).join(' · ');
}

// Load, parse and structure events
const events = Object.entries(mdModules).map(([filePath, rawContent]) => {
  const { metadata, body } = parseMarkdown(rawContent);

  // Resolve co-located image URL using imageModules glob mapping
  let resolvedImageUrl = '';
  if (metadata.image) {
    const imgKey = `/src/events/${metadata.image}`;
    resolvedImageUrl = imageModules[imgKey] || '';
  }

  return {
    ...metadata,
    dateObj: new Date(metadata.date),
    body,
    imageUrl: resolvedImageUrl
  };
});

// Separate into upcoming and past based on the current date
const now = new Date();
const upcomingEvents = events
  .filter(event => {
    const hasNoDate = !event.date || event.date === 'null' || isNaN(event.dateObj.getTime());
    return hasNoDate || event.dateObj >= now;
  })
  .sort((a, b) => {
    const aNoDate = !a.date || a.date === 'null' || isNaN(a.dateObj.getTime());
    const bNoDate = !b.date || b.date === 'null' || isNaN(b.dateObj.getTime());
    if (aNoDate && bNoDate) return 0;
    if (aNoDate) return 1; // Put TBC at the end
    if (bNoDate) return -1;
    return a.dateObj - b.dateObj;
  });

const pastEvents = events
  .filter(event => {
    const hasNoDate = !event.date || event.date === 'null' || isNaN(event.dateObj.getTime());
    return !hasNoDate && event.dateObj < now;
  })
  .sort((a, b) => b.dateObj - a.dateObj); // Descending order (most recent first)

// Render upcoming events
const upcomingContainer = document.getElementById('upcoming-events-container');
const upcomingCountEl = document.getElementById('upcoming-count');

if (upcomingContainer) {
  if (upcomingEvents.length === 0) {
    upcomingCountEl.textContent = 'None scheduled';
    upcomingContainer.innerHTML = `
      <div class="upcoming-note reveal">
        <p>Nothing on the calendar at this exact moment.</p>
        <span class="sub">New circles and workshops are planned season by season, across different cities and online. The surest way to hear first is to get in touch or follow along on Instagram.</span>
        <a class="cta" href="/contact/">Get in touch <span aria-hidden="true">→</span></a>
      </div>
    `;
  } else {
    upcomingCountEl.textContent = `${upcomingEvents.length} scheduled`;
    const gridHtml = upcomingEvents.map(event => {
      const formattedDate = formatUpcomingDate(event.date);
      const bookingUrl = event.booking_url || '/contact/';

      // Combine location components: name, city, country
      const locationParts = [];
      if (event.location_name) {
        if (event.location_link) {
          locationParts.push(`<a href="${event.location_link}" class="where" target="_blank" rel="noopener">${event.location_name}</a>`);
        } else {
          locationParts.push(`<span class="where">${event.location_name}</span>`);
        }
      }
      if (event.location_city) {
        locationParts.push(event.location_city);
      }
      if (event.location_country) {
        locationParts.push(event.location_country);
      }
      const locationHtml = locationParts.join(', ');

      return `
        <article class="event reveal">
          <div class="event-poster">
            ${event.imageUrl ? `<img src="${event.imageUrl}" alt="${event.title} poster" style="width: 100%; height: 100%; object-fit: cover;">` : ''}
          </div>
          <div class="event-body">
            <div class="event-date">${formattedDate}</div>
            <h3>${event.title}</h3>
            ${locationHtml ? `<div class="meta">${locationHtml}</div>` : ''}
            <p class="desc">${event.description || ''}</p>
            <a class="cta" href="${bookingUrl}">Reserve a place <span aria-hidden="true">→</span></a>
          </div>
        </article>
      `;
    }).join('');

    upcomingContainer.innerHTML = `<div class="events-grid">${gridHtml}</div>`;
  }
}

// Render past events
const pastContainer = document.getElementById('past-events-container');
const pastCountEl = document.getElementById('past-count');

if (pastContainer) {
  pastCountEl.textContent = `${pastEvents.length} events`;

  if (pastEvents.length === 0) {
    pastContainer.innerHTML = '<p class="reveal">No past events recorded.</p>';
  } else {
    pastContainer.innerHTML = pastEvents.map(event => {
      const metaStr = formatPastMeta(event);
      return `
        <figure class="poster-figure reveal">
          <div style="overflow: hidden; border-radius: 3px;">
            ${event.imageUrl ? `<img class="poster" src="${event.imageUrl}" alt="${event.title} poster" loading="lazy">` : ''}
          </div>
          <figcaption class="poster-caption">
            <span class="pc-title">${event.title}</span>
            <span class="pc-meta">${metaStr}</span>
          </figcaption>
        </figure>
      `;
    }).join('');
  }
}

// Scroll-reveal animations observer logic
const io = new IntersectionObserver((entries) => {
  entries.forEach((e) => {
    if (e.isIntersecting) {
      e.target.classList.add('in');
      io.unobserve(e.target);
    }
  });
}, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

// Observe the elements on the page (including dynamically generated ones)
document.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => io.observe(el));
